import type { BrandSpecials } from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { Flex, Typography } from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { DMMenuRow } from '@jane/bloom/components/menuRow/dmMenuRow';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  StoreSpecial,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { NavigationCategory } from '@jane/bloom/types/schemas/roots/product';
import { formatProductCount } from '@jane/bloom/utils/strings/formatProductCount';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { AlgoliaProduct } from '@jane/search/types';

import styles from './categoryTab.module.css';

type DefaultCategoryTabProps = {
  brandSpecials: BrandSpecials;
  category: NavigationCategory | undefined;
  customCategoryLabels: Record<string, string>;
  customLanguage: CustomLanguage;
  dynamicPlacement?: SmartSort<AlgoliaProduct>;
  enabledSpecialsForRow: StoreSpecial[];
  featuredTab?: boolean;
  janeGold: JaneGold;
  menuTopRowInstance?: SmartSort<AlgoliaProduct>;
  pauseRefetching: () => void;
  preloadedProductIds: number[];
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  specials: StoreSpecials;
  unpauseRefetching: () => void;
  wizardBannerSlot?: ReactNode;
};

export function CategoryTab({
  brandSpecials,
  category,
  customCategoryLabels,
  customLanguage,
  dynamicPlacement,
  enabledSpecialsForRow,
  featuredTab,
  janeGold,
  menuTopRowInstance,
  pauseRefetching,
  preloadedProductIds,
  rowInstance,
  specials,
  unpauseRefetching,
  wizardBannerSlot,
}: DefaultCategoryTabProps) {
  const { skeletonLoader } = useSkeletonLoader();

  const hasResults = useMemo(
    () => (rowInstance?.products?.length ?? 0) > 0,
    [rowInstance?.products?.length]
  );

  if (!rowInstance) {
    return <></>;
  }

  if (skeletonLoader) {
    return skeletonLoader;
  }

  return (
    <main className="main">
      {!featuredTab && (
        <div className={styles['categoryTabContainer']}>
          {wizardBannerSlot && (
            <div className={styles['wizardBanner']}>{wizardBannerSlot}</div>
          )}
          <Typography
            color="inherit"
            variant="h2-semibold"
            className="product-count"
            as="h2"
          >
            {category ? customCategoryLabels[category] : 'Shop all'}
          </Typography>
        </div>
      )}

      {!hasResults && (
        <Flex justifyContent="center" alignItems="center" height="500px">
          <Typography
            color="inherit"
            variant="h1-semibold"
            data-testid="no-results"
          >
            No Products Available
          </Typography>
        </Flex>
      )}

      {menuTopRowInstance && (
        <DMMenuRow
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={brandSpecials}
          menuTopRow={menuTopRowInstance}
          customLanguage={customLanguage}
          specials={enabledSpecialsForRow}
          janeGold={janeGold}
          embedInProductGrid={true}
        />
      )}

      {hasResults && (
        <Typography color="grays-mid" className="product-count">
          {formatProductCount(rowInstance?.totalProducts)}
        </Typography>
      )}

      <ProductGrid
        brandSpecials={brandSpecials}
        customLanguage={customLanguage}
        dynamicPlacement={dynamicPlacement}
        key={category}
        preloadedProductIds={preloadedProductIds}
        rowInstance={rowInstance}
        specials={specials}
        janeGold={janeGold}
      />
    </main>
  );
}
